import React, { useState } from "react";
import {
  Button,
  Typography,
  Stack,
  Divider,
  Popover,
  MenuItem,
  Box,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import Treeview from "./Treeview";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FolderIcon from "@mui/icons-material/Folder";
import FolderCopyIcon from "@mui/icons-material/FolderCopy";
import { CollectionIcon } from "assets/icons/CollectionsIcon";
import { useSelector } from "react-redux";
import SideNavLink from "components/base/SideNavLink";
import { useDispatch } from "react-redux";
import { setIsBookmarkSidebar, setSubPopup } from "context/features/dataSlice";
import { useEffect } from "react";
import { SubmissionFolderNav } from "features/Submissions/components";
import { getMySubmissions } from "features/Submissions/services";
import { SUBMISSION_FOLDERS_UPDATED_EVENT } from "features/Submissions/hooks";

export default function SideMenu({ setNewPopup }) {
  // const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [currentCollection, setCurrentCollection] = useState("");
  const [archivesExpanded, setArchivesExpanded] = useState(false);
  const [submittedCollectionIds, setSubmittedCollectionIds] = useState(new Set());
  const { collections, collectionIdArray, archivedCollections } = useSelector(
    (state) => state.data
  );
  const { groupRole, groupId, gradeLicenseLevel } = useSelector((state) => state.account);
  const canManageSubmissions = groupId != null && (
    ["Teacher", "OrganizationAdmin", "Administrator", "Admin"].includes(groupRole) ||
    gradeLicenseLevel === "group-educator"
  );
  const isStudent = groupRole === "Student";

  useEffect(() => {
    if (!isStudent) {
      setSubmittedCollectionIds(new Set());
      return undefined;
    }

    const loadSubmittedCollections = async () => {
      try {
        const response = await getMySubmissions();
        const submissions = Array.isArray(response)
          ? response
          : response?.submissions || response?.items || [];
        setSubmittedCollectionIds(new Set(submissions.map((submission) => Number(
          submission.collectionID_lng ?? submission.collectionID ?? submission.collection_id
        ))));
      } catch {
        setSubmittedCollectionIds(new Set());
      }
    };

    loadSubmittedCollections();
    window.addEventListener(SUBMISSION_FOLDERS_UPDATED_EVENT, loadSubmittedCollections);
    return () => window.removeEventListener(SUBMISSION_FOLDERS_UPDATED_EVENT, loadSubmittedCollections);
  }, [isStudent]);

  const collectionsWithSubmissionState = collections.map((collection) => ({
    ...collection,
    isSubmitted_ysn: submittedCollectionIds.has(Number(collection.collectionId)),
  }));

  const dispatch = useDispatch();

  const theme = useTheme();

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const isSubmissionsPath = pathname.startsWith("/collections/submissions");

  const tabStyle = (active) => ({
    color: theme.palette.text.primary,
    fontStyle: "italic",
    padding: "0 0 0.55rem",
    textDecoration: "none",
    borderBottom: `2px solid ${active ? theme.palette.primary.main : "transparent"}`,
  });

  const checkRecentAndPublic = () => {
    if (pathname.includes("/collections/public")) {
      return true;
    }
    if (params.id) {
      return false;
    }
    return true;
  };

  const handlePopoverOpen = (event) => {
    if (pathname.startsWith("/collections/submissions")) {
      navigate("/collections/submissions", { state: { openCreate: true } });
      return;
    }
    if (checkRecentAndPublic()) {
      setNewPopup(true);
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  // const { isBookmarkSidebar } = useSelector((state) => state.data);

  const isPopoverOpen = Boolean(anchorEl);

  useEffect(() => {
    if (params.id) {
      const currentCollection = collections.filter(
        (collection) => Number(collection.collectionId) === Number(params.id)
      )[0];
      setCurrentCollection(currentCollection);
    }
  }, [params.id]);

  useEffect(() => {
    if (pathname.includes("/collections/archive")) {
      setArchivesExpanded(true);
    }
  }, [pathname]);

  return (
    <React.Fragment>
      <Box sx={{ ml: 3, pt: 2 }}>
        <Stack gap={1}>
          <Button
            size="large"
            startIcon={<AddIcon />}
            onClick={handlePopoverOpen}
            sx={{
              justifyContent: "flex-start",
              fontSize: "2.3rem",
              lineHeight: "3rem",
              mr: 3,
            }}
          >
            New
          </Button>
          <Popover
            anchorEl={anchorEl}
            open={isPopoverOpen}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            sx={{
              transform: `translateY(10%)`,
            }}
            disableScrollLock={true}
          >
            <MenuItem
              sx={{
                display: "flex",
                gap: 2,
                p: 2,
                mt: 2,
                "&:hover": {
                  backgroundColor: theme.palette.secondary.main,
                },
              }}
              onClick={() => {
                setNewPopup(true);
                handlePopoverClose();
              }}
            >
              <FolderIcon sx={{ color: "primary.main" }} />
              <Typography variant="body1">New Collection</Typography>
            </MenuItem>
            {currentCollection != null && collectionIdArray.length < 3 && (
              <MenuItem
                disabled={currentLevel === 2 ? true : false}
                sx={{
                  display: "flex",
                  gap: 2,
                  p: 2,
                  "&:hover": {
                    backgroundColor: theme.palette.secondary.main,
                  },
                }}
                onClick={() => {
                  dispatch(setSubPopup(true));
                  handlePopoverClose();
                }}
              >
                <FolderCopyIcon sx={{ color: "primary.main" }} />

                <Typography variant="body1">
                  {`Subfolder in ${currentCollection.name}`}
                </Typography>
              </MenuItem>
            )}
            <MenuItem
              sx={{
                display: "flex",
                gap: 2,
                p: 2,
                mb: 2,
                "&:hover": {
                  backgroundColor: theme.palette.secondary.main,
                },
              }}
              onClick={() => {
                // setNewBookmarkPopup(true);
                handlePopoverClose();
                dispatch(setIsBookmarkSidebar(true));
              }}
            >
              <CollectionIcon sx={{
                  width: 20,
                  height: 20,
                  color: "primary.main",
              }}/>
              <Typography variant="body1">New External Bookmark</Typography>
            </MenuItem>
          </Popover>
          {canManageSubmissions && (
            <Stack direction="row" gap={3} sx={{ ml: 1, mt: 1.3, mb: 1 }}>
              <SideNavLink
                to="/collections"
                end={false}
                className="parallel-nav-tab"
                style={() => tabStyle(!isSubmissionsPath)}
              >
                Collections
              </SideNavLink>
              <SideNavLink
                to="/collections/submissions"
                end={false}
                className="parallel-nav-tab"
                style={() => tabStyle(isSubmissionsPath)}
              >
                Submissions
              </SideNavLink>
            </Stack>
          )}
          {isSubmissionsPath && canManageSubmissions ? (
            <React.Fragment>
              <SubmissionFolderNav />
              <Divider sx={{ width: "90%" }} />
              <Typography variant="h6" color="primary.dark" fontStyle="italic">
                Public Collections
              </Typography>
              <SideNavLink to="/collections/public">Explore</SideNavLink>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <SideNavLink to="/collections">Recent Activity</SideNavLink>
              <Treeview
                collections={collectionsWithSubmissionState}
                collectionIdArray={collectionIdArray}
                setCurrentLevel={setCurrentLevel}
                maxHeight={archivedCollections.length > 0 ? "35vh" : "50vh"}
              />
              <Divider sx={{ width: "90%" }} />
              <Typography variant="h6" color="primary.dark" fontStyle="italic">
                Public Collections
              </Typography>
              <SideNavLink to="/collections/public">Explore</SideNavLink>
              {archivedCollections.length > 0 && (
                <React.Fragment>
                  <Divider sx={{ width: "90%" }} />
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{ pr: 3, cursor: "pointer" }}
                    onClick={() => setArchivesExpanded((prev) => !prev)}
                  >
                    {archivesExpanded ? (
                      <ExpandMoreIcon sx={{ color: "primary.main" }} />
                    ) : (
                      <ChevronRightIcon sx={{ color: "primary.main" }} />
                    )}
                    <ArchiveOutlinedIcon
                      sx={{ color: "primary.main", width: 18, height: 18 }}
                    />
                    <Typography variant="h6" color="primary.dark" fontStyle="italic">
                      Archives
                    </Typography>
                  </Stack>
                  {archivesExpanded && (
                    <Treeview
                      collections={archivedCollections}
                      collectionIdArray={[]}
                      setCurrentLevel={() => {}}
                      basePath="/collections/archive"
                      maxHeight="20vh"
                    />
                  )}
                </React.Fragment>
              )}
            </React.Fragment>
          )}
        </Stack>
      </Box>
    </React.Fragment>
  );
}
